import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { COUNTRY_DIVISIONS, getCountry } from '../services/country';
import JobCard from '../components/jobs/JobCard';
import PageSeo, { getMarketSeo } from '../components/seo/PageSeo';
import styles from './Home.module.css';

const CAT_ICONS = { 'ti-code':'💻','ti-speakerphone':'📣','ti-palette':'🎨','ti-chart-bar':'📊','ti-heart':'🏥','ti-school':'🎓','ti-users':'👥','ti-shopping-cart':'🛒','ti-building':'🏢','ti-tool':'🔧','ti-beach':'🏖️','ti-gavel':'⚖️','ti-truck':'🚛','ti-headset':'🎧','ti-cash':'💰' };

export default function Home() {
  const country=getCountry();
  const pt=country.code==='BR';
  const seo=getMarketSeo(country);
  const homeStructuredData={
    '@context':'https://schema.org','@graph':[
      {'@type':'Organization','@id':'https://talentosrd.com/#organization',name:'TalentosRD',url:'https://talentosrd.com',logo:'https://talentosrd.com/icon.svg',email:'info@talentosrd.com'},
      {'@type':'WebSite','@id':'https://talentosrd.com/#website',name:'TalentosRD',url:'https://talentosrd.com',publisher:{'@id':'https://talentosrd.com/#organization'},inLanguage:country.locale},
    ],
  };
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [popularJobs,setPopularJobs]=useState([]);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/categories'),
      api.get('/jobs/featured'),
      api.get('/jobs/popular'),
    ]).then(([cats, feat, popular]) => {
      // Aseguramos que los datos sean siempre arreglos
      setCategories(Array.isArray(cats.data) ? cats.data : []);
      setFeatured(Array.isArray(feat.data) ? feat.data : []);
      setPopularJobs(Array.isArray(popular.data)?popular.data:[]);
    }).catch(err => {
      console.error("Error al cargar datos iniciales:", err);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (search.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get('/search/suggestions', { params: { q: search } }).then(r => setSuggestions(r.data));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (city) p.set('province', city);
    navigate(`/jobs?${p}`);
  };

  return (
    <div className={styles.page}>
      <PageSeo title={seo.homeTitle} description={seo.homeDescription} alternates structuredData={homeStructuredData}/>
      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroContent}>
            <div className={styles.heroLeft}>
              <span className={styles.heroEyebrow}>{country.flag} {pt?'Oportunidades feitas para você':'Oportunidades hechas para ti'}</span>
              <h1>{pt?<>Encontre as melhores <span>vagas de emprego</span> no Brasil</>:<>Encuentra los mejores <span>empleos y trabajos</span> en {country.name}</>}</h1>
              <p>{pt?'Explore oportunidades de trabalho e conecte-se com empresas em um só lugar.':'Explora oportunidades laborales y conecta con empresas desde un solo lugar.'}</p>
              <form onSubmit={handleSearch} className={styles.searchForm}>
                <div className={styles.searchGroup} ref={searchRef}>
                  <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  <input
                    aria-label={pt?'Cargo, empresa ou habilidade':'Cargo, empresa o habilidad'}
                    value={search}
                    onChange={e => { setSearch(e.target.value); setShowSug(true); }}
                    onFocus={() => setShowSug(true)}
                    placeholder={pt?'Cargo, empresa ou habilidade...':'Cargo, empresa o habilidad...'}
                    className={styles.searchInput}
                  />
                  {showSug && suggestions.length > 0 && (
                    <div className={styles.suggestions}>
                      {suggestions.map((s,i) => (
                        <button key={i} type="button" onClick={() => { setSearch(s.text); setShowSug(false); }}>
                          <span className={styles.sugType}>{s.type === 'job' ? '💼' : '📂'}</span>
                          {s.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.searchGroup}>
                  <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  <select aria-label={country.divisionSingular} value={city} onChange={e => setCity(e.target.value)} className={styles.searchInput}>
                    <option value="">{pt?'Todos os':'Todos los'} {country.divisionPlural}</option>
                    {COUNTRY_DIVISIONS.map(province=><option key={province} value={province}>{province}</option>)}
                  </select>
                </div>
                <button type="submit" className={`btn btn-primary btn-lg ${styles.searchBtn}`}>{pt?'Buscar vagas':'Buscar empleos'}</button>
              </form>
              <div className={styles.popular}>
                <span>{pt?'Em alta:':'Popular:'}</span>
                {(pt?['Desenvolvedor','Marketing','Design','Vendas','Contabilidade']:['Desarrollador','Marketing','Diseño','Ventas','Contador']).map(t => (
                  <button key={t} onClick={()=>navigate(`/jobs?search=${t}`)}>{t}</button>
                ))}
              </div>
              <div className={styles.heroTrust}>
                <span>✓ {pt?'Cadastro gratuito':'Registro gratuito'}</span>
                <span>✓ {pt?'Empresas verificadas':'Empresas verificadas'}</span>
                <span>✓ {pt?'Candidatura simples':'Postulación sencilla'}</span>
              </div>
            </div>
            <aside className={styles.heroActions} aria-label="Comenzar en TalentosRD">
              <div className={styles.actionCard}>
                <span className={styles.actionIcon}>👤</span>
                <div><small>{pt?'Para candidatos':'Para candidatos'}</small><h2>{pt?'Encontre sua próxima oportunidade':'Encuentra tu próxima oportunidad'}</h2><p>{pt?'Crie seu perfil profissional e organize suas candidaturas.':'Crea tu perfil profesional y organiza tus postulaciones.'}</p></div>
                <Link to="/register?role=candidate" className={styles.actionLink}>{pt?'Criar meu perfil':'Crear mi perfil'} <span>→</span></Link>
              </div>
              <div className={`${styles.actionCard} ${styles.actionCardCompany}`}>
                <span className={styles.actionIcon}>🏢</span>
                <div><small>{pt?'Para empresas':'Para empresas'}</small><h2>{pt?'Conecte-se com novos talentos':'Conecta con nuevos talentos'}</h2><p>{pt?'Cadastre sua empresa e publique sua primeira vaga.':'Registra tu empresa y publica tu primera vacante.'}</p></div>
                <Link to="/register?role=company" className={styles.actionLink}>{pt?'Publicar uma vaga':'Publicar una vacante'} <span>→</span></Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Categorías */}
      <section className={styles.section}>
        <div className="container">
          <div className={styles.sectionHead}>
            <h2>{pt?'Explorar por área profissional':'Explorar por área profesional'}</h2>
            <Link to="/jobs" className={styles.seeAll}>{pt?'Ver todas as vagas':'Ver todas las ofertas'} →</Link>
          </div>
          <div className={styles.catGrid}>
            {Array.isArray(categories) && categories.length > 0 ? (
                categories.map(cat => (
                  <Link key={cat.id} to={`/jobs?category=${cat.slug}`} className={styles.catCard}>
                    <span className={styles.catEmoji}>{CAT_ICONS[cat.icon] || '📁'}</span>
                    <span className={styles.catName}>{cat.name}</span>
                    <span className={styles.catCount}>{pt?'Explorar oportunidades':'Explorar oportunidades'}</span>
                  </Link>
                ))
            ) : (
                <p>{pt?'Carregando categorias...':'Cargando categorías...'}</p>
            )}
          </div>
        </div>
      </section>

      <section className={styles.seoSection} aria-labelledby="seo-jobs-heading">
        <div className="container">
          <div className={styles.seoContent}>
            <article>
              <h2 id="seo-jobs-heading">{pt?'Vagas de emprego no Brasil':`Empleos en ${country.name}`}</h2>
              <p>{pt
                ?`TalentosRD é um portal onde empresas publicam oportunidades de trabalho no Brasil. Encontre vagas em ${seo.cities.join(', ')} e outras cidades.`
                :`TalentosRD es un portal donde empresas publican ofertas de trabajo en ${country.name}. Encuentra empleos en ${seo.cities.join(', ')} y otras ciudades.`}</p>
            </article>
            <article>
              <h2>{pt?'Vagas por categoria':'Trabajos por categoría'}</h2>
              <p>{pt
                ?'Busque oportunidades em tecnologia, vendas, administração, contabilidade, engenharia, turismo e muitas outras áreas profissionais.'
                :'Busca trabajos de informática, ventas, administración, contabilidad, ingeniería, turismo y muchas áreas profesionales.'}</p>
            </article>
          </div>
        </div>
      </section>

      {/* Ranking de empleos */}
      {popularJobs.length>0&&<section className={styles.popularJobsSection}>
        <div className="container">
          <div className={styles.sectionHead}>
            <div><span className={styles.rankingEyebrow}>{pt?'Tendências no':'Tendencias en'} {country.name}</span><h2>{pt?'Top 10 vagas mais procuradas':'Top 10 empleos más buscados'}</h2></div>
            <Link to="/jobs" className={styles.seeAll}>{pt?'Explorar vagas':'Explorar empleos'} →</Link>
          </div>
          <div className={styles.topJobsGrid}>
            {popularJobs.map((job,index)=><Link to={`/jobs/${job.id}`} className={styles.topJob} key={job.id}>
              <span className={styles.topRank}>{String(index+1).padStart(2,'0')}</span>
              <span className={styles.topJobBody}><strong>{job.title}</strong><small>{job.company_name} · {job.city||job.province}</small></span>
              <span className={styles.topJobMeta}>{job.modality==='remote'?'Remoto':job.modality==='hybrid'?'Híbrido':'Presencial'}{job.is_demo&&<em>Demo</em>}</span>
              <span className={styles.topArrow}>→</span>
            </Link>)}
          </div>
        </div>
      </section>}

      {/* Empleos destacados */}
      <section className={styles.sectionAlt}>
        <div className="container">
          <div className={styles.sectionHead}>
            <h2>⭐ {pt?'Vagas em destaque':'Empleos destacados'}</h2>
            <Link to="/jobs?is_featured=true" className={styles.seeAll}>{pt?'Ver todas':'Ver todos'} →</Link>
          </div>
          {loading ? (
            <div className="page-loading"><div className="spinner"/><span>{pt?'Carregando...':'Cargando...'}</span></div>
          ) : (
            <div className={styles.jobsGrid}>
              {Array.isArray(featured) && featured.length > 0 ? featured.map(job => <JobCard key={job.id} job={job} />) : (
                <div className={styles.emptyJobs}>
                  <span>💼</span>
                  <h3>{pt?'Novas oportunidades em breve':'Nuevas oportunidades muy pronto'}</h3>
                  <p>{pt?'Explore todas as vagas disponíveis enquanto chegam novos destaques.':'Explora todas las vacantes disponibles mientras llegan nuevos empleos destacados.'}</p>
                </div>
              )}
            </div>
          )}
          <div className={styles.browseMore}>
            <Link to="/jobs" className="btn btn-outline btn-lg">{pt?'Ver todas as vagas':'Ver todos los empleos'}</Link>
          </div>
        </div>
      </section>

      {/* Para empresas */}
      <section className={styles.companyBanner}>
        <div className="container">
          <div className={styles.bannerInner}>
            <div>
              <h2>{pt?'Procurando o talento ideal para sua empresa?':'¿Buscas el talento ideal para tu empresa?'}</h2>
              <p>{pt?`Publique suas vagas e encontre candidatos qualificados no ${country.name}. Planos a partir de ${country.currency} 0.`:`Publica tus ofertas de empleo y accede a candidatos cualificados de ${country.name}. Planes desde ${country.currency} 0.`}</p>
              <div className={styles.bannerBtns}>
                <Link to="/register?role=company" className="btn btn-primary btn-lg">{pt?'Publicar vaga grátis':'Publicar empleo gratis'}</Link>
                <Link to="/pricing" className="btn btn-outline btn-lg" style={{color:'#fff',borderColor:'#fff'}}>{pt?'Ver planos e preços':'Ver planes y precios'}</Link>
              </div>
            </div>
            <div className={styles.bannerFeatures}>
              {(pt?['✓ Acesso a candidatos verificados','✓ Painel de gestão avançado','✓ Filtros de busca inteligentes','✓ Estatísticas em tempo real','✓ Suporte dedicado']:['✓ Acceso a candidatos verificados','✓ Panel de gestión avanzado','✓ Filtros de búsqueda inteligentes','✓ Estadísticas en tiempo real','✓ Soporte dedicado']).map((f,i) => (
                <div key={i} className={styles.feat}>{f}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className={styles.section}>
        <div className="container">
          <h2 style={{textAlign:'center',marginBottom:'2.5rem'}}>{pt?'Como funciona a TalentosRD?':'¿Cómo funciona TalentosRD?'}</h2>
          <div className={styles.steps}>
            {(pt?[
              {n:'1', title:'Crie seu perfil', desc:'Cadastre-se gratuitamente e complete seu perfil com experiência, habilidades e expectativas salariais.', icon:'👤'},
              {n:'2', title:'Explore vagas', desc:'Encontre vagas com filtros por área, salário, modalidade e estado.', icon:'🔍'},
              {n:'3', title:'Candidate-se', desc:'Envie sua candidatura com um clique e anexe seu currículo atualizado.', icon:'📤'},
              {n:'4', title:'Conquiste a vaga!', desc:'As empresas analisam seu perfil e entram em contato. Gerencie tudo no seu painel.', icon:'🎉'},
            ]:[
              {n:'1', title:'Crea tu perfil', desc:'Regístrate gratis y completa tu perfil con tu experiencia, habilidades y expectativas salariales.', icon:'👤'},
              {n:'2', title:'Explora ofertas', desc:'Busca entre miles de ofertas con filtros avanzados por área, salario, modalidad y provincia.', icon:'🔍'},
              {n:'3', title:'Postúlate', desc:'Envía tu postulación con un click. Adjunta carta de presentación y tu CV actualizado.', icon:'📤'},
              {n:'4', title:'¡Consigue el empleo!', desc:'Las empresas revisan tu perfil y te contactan. Gestiona todo desde tu dashboard personal.', icon:'🎉'},
            ]).map((s,i) => (
              <div key={i} className={styles.step}>
                <div className={styles.stepNum}>{s.icon}</div>
                <div className={styles.stepDot}>{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
